import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '@/common/SafeIcon';
import { triggerChat } from '@/utils/chatTrigger';

const ArticleModal = ({ post, isOpen, onClose }) => {
  if (!post) return null;

  const handleBooking = () => {
    onClose();
    // Small delay to allow modal close animation before triggering chat
    setTimeout(() => {
      triggerChat();
    }, 300);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[10001] flex items-center justify-center p-4 md:p-8">
          {/* Backdrop */}
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-navy/98 backdrop-blur-xl"
          />

          {/* Modal Content */}
          <motion.div 
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="relative w-full max-w-4xl bg-navy-lighter border border-white/10 rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]"
          >
            {/* Close Button */}
            <button 
              onClick={onClose}
              className="absolute top-6 right-6 z-10 p-2 bg-white/5 hover:bg-white/10 rounded-full text-white transition-colors"
            >
              <SafeIcon icon={FiIcons.FiX} className="text-2xl" />
            </button>

            {/* Scrollable Area */}
            <div className="overflow-y-auto p-8 md:p-16 custom-scrollbar">
              {/* Header Info */}
              <div className="flex items-center gap-3 text-gold text-xs font-bold uppercase tracking-[0.2em] mb-6">
                <SafeIcon icon={FiIcons.FiCalendar} className="text-sm" />
                <span>{post.displayDate}</span>
                <span className="w-1 h-1 rounded-full bg-white/20" />
                <span>5 Min Read</span>
              </div>

              {/* Title */}
              <h2 className="text-4xl md:text-6xl font-anton uppercase tracking-wide text-white mb-10 leading-tight">
                {post.headline}
              </h2>

              {/* Progress Line */}
              <div className="w-20 h-1 bg-gold mb-12 rounded-full" />

              {/* Article Content */}
              <div className="prose prose-invert max-w-none">
                {post.article.split('\n').map((paragraph, idx) => (
                  paragraph.trim() && (
                    <p key={idx} className="text-lg md:text-xl text-gray-300 leading-relaxed mb-6 font-medium">
                      {paragraph}
                    </p>
                  )
                ))}
              </div>

              {/* Bottom CTA */}
              <div className="mt-16 p-8 rounded-2xl bg-white/5 border border-white/10 flex flex-col md:flex-row items-center justify-between gap-8">
                <div>
                  <h4 className="text-2xl font-anton uppercase text-white mb-2">Ready to automate?</h4>
                  <p className="text-gray-400">Talk to our live agents right now to see how it works.</p>
                </div>
                <button 
                  onClick={handleBooking}
                  className="px-8 py-4 bg-gold text-navy font-bold uppercase tracking-widest rounded-lg hover:bg-gold-hover transition-all whitespace-nowrap"
                >
                  Book Now
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default ArticleModal;